import html2canvas from 'html2canvas'
import { toPng } from 'html-to-image'
import QRCode from 'qrcode'
import { createDRCode, openAppPage } from 'dad-h5-common-js'

function wait(ms = 16) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function waitImageLoaded(img, timeoutMs = 5000) {
  if (!img) return
  if (!img.src) return
  if (img.complete && img.naturalWidth > 0) return
  await new Promise((resolve) => {
    let timer = null
    const done = () => {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      img.removeEventListener('load', done)
      img.removeEventListener('error', done)
      resolve()
    }
    timer = setTimeout(() => {
      console.warn('[share] image load timeout:', img.src)
      done()
    }, timeoutMs)
    img.addEventListener('load', done)
    img.addEventListener('error', done)
  })
}

export async function waitForAssets(el) {
  if (!el) return
  const images = Array.from(el.querySelectorAll('img'))
  await Promise.all(images.map(img => waitImageLoaded(img, 5000)))
  if (document.fonts?.ready) {
    await document.fonts.ready
  }
}

export async function buildQrCode(link = '') {
  if (!link) return ''
  try {
    return await QRCode.toDataURL(link, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 180,
    })
  }
  catch (error) {
    console.error('[share] build qr failed:', error)
    return ''
  }
}

function stripDataUrlPrefix(dataUrl = '') {
  const idx = dataUrl.indexOf(',')
  if (idx < 0) return dataUrl
  return dataUrl.slice(idx + 1)
}

export async function capturePoster(el, options = {}) {
  if (!el) throw new Error('Poster element is required')
  await waitForAssets(el)
  await wait(30)

  const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
  const targetScale = options.scale || pixelRatio
  const bgColor = options.backgroundColor || '#ffffff'

  try {
    const canvas = await html2canvas(el, {
      scale: targetScale,
      useCORS: true,
      backgroundColor: bgColor,
      windowWidth: el.scrollWidth,
      windowHeight: el.scrollHeight,
    })
    return canvas.toDataURL('image/png')
  }
  catch (error) {
    console.warn('[share] html2canvas failed, fallback to html-to-image', error)
    try {
      return await toPng(el, {
        cacheBust: true,
        pixelRatio: targetScale,
        backgroundColor: bgColor,
      })
    }
    catch (fallbackError) {
      if (targetScale > 1.25) {
        const canvas = await html2canvas(el, {
          scale: 1.25,
          useCORS: true,
          backgroundColor: bgColor,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
        })
        return canvas.toDataURL('image/png')
      }
      throw fallbackError
    }
  }
}

function hasNativeAppUA() {
  const ua = window?.navigator?.userAgent || ''
  return ua.includes('EZPayAPP') || ua.includes('Flutter')
}

function isAppContainer(deviceType = '') {
  // 仅根据 deviceType 在本地 dev 环境会误判，需结合真实 UA 判断
  if (!hasNativeAppUA()) return false
  return ['android', 'ios'].includes(String(deviceType || '').toLowerCase())
}

function toVersionNumber(version) {
  const value = Number(version)
  if (Number.isNaN(value)) return 0
  return value
}

function downloadImageOnWeb(dataUrl, fileName = 'share.png') {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export async function downloadImageByPlatform({
  base64,
  fileName = 'share.png',
  deviceType = '',
  version = 0,
  shareLink = '',
}) {
  if (!base64) {
    throw new Error('Empty image data')
  }
  const appEnv = isAppContainer(deviceType)
  if (appEnv) {
    const parsedVersion = toVersionNumber(version)
    if (parsedVersion >= 3110) {
      await openAppPage({
        type: 'downloadImg',
        data: {
          base64,
          fileName,
        },
      })
      return
    }
    createDRCode(shareLink || '', 'qrCode')
    return
  }

  try {
    downloadImageOnWeb(`data:image/png;base64,${base64}`, fileName)
  }
  catch (error) {
    console.error('[share] web download failed:', error)
    const opened = window.open(`data:image/png;base64,${base64}`, '_blank')
    if (!opened) {
      throw error
    }
  }
}

export async function shareToNative(channel, payload = {}, platform = {}) {
  if (!isAppContainer(platform.deviceType)) return false
  try {
    await openAppPage({
      type: 'share',
      data: {
        channel,
        title: payload.title || '',
        desc: payload.desc || '',
        link: payload.link || '',
        scene: payload.scene || '',
      },
    })
    return true
  }
  catch (error) {
    console.error('[share] native share failed:', error)
    return false
  }
}

export function dataUrlToBase64(dataUrl = '') {
  return stripDataUrlPrefix(dataUrl)
}
